import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, LogOut, RefreshCw, Search, User, Clock, MapPin, Activity, ArrowLeft, X } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import useGlobalSearch from '../../hooks/useGlobalSearch';

const CaseLogList = () => {
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const { searchQuery: searchTerm, setSearchQuery: setSearchTerm, debouncedSearchQuery: debouncedSearch, searchInputRef } = useGlobalSearch('', 400);
    const [moduleFilter, setModuleFilter] = useState('');

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;
    const [stats, setStats] = useState({
        totalLogs: 0,
        loginCount: 0,
        logoutCount: 0,
        activeUsers: 0
    });

    useEffect(() => {
        fetchLogs();
        setCurrentPage(1);
    }, [moduleFilter]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    // Pagination calculations — moved below filteredLogs declaration

    const fetchLogs = async () => {
        try {
            setLoading(true);
            const params = {};
            if (moduleFilter) params.module = moduleFilter;

            const { data } = await api.get('/logs', { params });
            if (data.success) {
                setLogs(data.data);
                calculateStats(data.data);
            }
        } catch (error) {
            toast.error('Failed to fetch logs');
            console.error('Error fetching logs:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const calculateStats = (logsData) => {
        const loginCount = logsData.filter(log => log.action === 'Login').length;
        const logoutCount = logsData.filter(log => log.action === 'Logout').length;
        const uniqueUsers = new Set(logsData.map(log => log.user?._id).filter(Boolean)).size;

        setStats({
            totalLogs: logsData.length,
            loginCount,
            logoutCount,
            activeUsers: uniqueUsers
        });
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchLogs();
        toast.success('Logs refreshed!');
    };

    const filteredLogs = logs.filter(log => {
        if (!debouncedSearch) return true;
        const query = debouncedSearch.toLowerCase();
        return (
            log.user?.fullName?.toLowerCase().includes(query) ||
            log.user?.email?.toLowerCase().includes(query) ||
            log.action?.toLowerCase().includes(query) ||
            log.details?.toLowerCase().includes(query)
        );
    });

    // Pagination calculations — depend on filteredLogs so must be declared after it
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentLogs = filteredLogs.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);

    const getActionIcon = (action) => {
        if (action === 'Login') return <LogIn className="w-4 h-4 text-green-600" />;
        if (action === 'Logout') return <LogOut className="w-4 h-4 text-red-600" />;
        return <Activity className="w-4 h-4 text-blue-600" />;
    };

    const getActionBadge = (action) => {
        const badges = {
            Login: { bg: 'bg-green-100', text: 'text-green-800' },
            Logout: { bg: 'bg-red-100', text: 'text-red-800' },
        };
        const badge = badges[action] || { bg: 'bg-blue-100', text: 'text-blue-800' };
        return (
            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                {getActionIcon(action)}
                {action}
            </span>
        );
    };

    const StatCard = ({ icon: Icon, label, value, subtext, gradient, loading }) => (
        <div className="group relative bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/35 p-4 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] overflow-hidden">
            <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
            <div className="relative flex items-center justify-between">
                <div className="flex-1">
                    <p className="text-[10px] font-semibold text-gray-500 dark:text-slate-300 uppercase tracking-wide mb-1">{label}</p>
                    {loading ? (
                        <div className="h-6 bg-gray-200 rounded animate-pulse w-12"></div>
                    ) : (
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
                    )}
                    <p className="text-xs text-gray-400 dark:text-slate-400 mt-1">{subtext}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="text-white" size={18} />
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 p-4 lg:p-6">
            {/* Header */}
            <div className="glass rounded-xl shadow-md border border-white/30 p-4 mb-6 animate-fade-in">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 rounded-lg bg-white shadow-sm border border-gray-200 text-gray-600 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-all"
                            title="Go Back"
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold gradient-text mb-1">Case Log - Activity Tracker</h1>
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                                <Activity size={14} className="text-indigo-500" />
                                <span className="font-medium">Monitor user login and logout activity</span>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="btn-primary flex items-center gap-2 text-sm px-3 py-2"
                    >
                        <RefreshCw className={refreshing ? 'animate-spin' : ''} size={14} />
                        <span>{refreshing ? 'Refreshing...' : 'Refresh Logs'}</span>
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard
                    icon={Activity}
                    label="Total Logs"
                    value={stats.totalLogs}
                    subtext="All recorded activities"
                    gradient="from-[#00152E] to-[#1a3884]"
                    loading={loading}
                />
                <StatCard
                    icon={LogIn}
                    label="Login Events"
                    value={stats.loginCount}
                    subtext="Successful logins"
                    gradient="from-[#00152E] to-[#1a3884]"
                    loading={loading}
                />
                <StatCard
                    icon={LogOut}
                    label="Logout Events"
                    value={stats.logoutCount}
                    subtext="User logouts"
                    gradient="from-[#00152E] to-[#1a3884]"
                    loading={loading}
                />
                <StatCard
                    icon={User}
                    label="Active Users"
                    value={stats.activeUsers}
                    subtext="Unique users tracked"
                    gradient="from-[#00152E] to-[#1a3884]"
                    loading={loading}
                />
            </div>

            {/* Filters */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-6">
                <div className="flex flex-col lg:flex-row flex-wrap items-start lg:items-center gap-3 w-full">
                    {/* Search - Primary Control */}
                    <div className="relative w-full lg:flex-1 min-w-[250px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search by user, email, or action..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1a3884] bg-gray-50 focus:bg-white transition-all outline-none"
                        />
                        {searchTerm && (
                            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {/* Filters Group */}
                    <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
                        <select
                            value={moduleFilter}
                            onChange={(e) => setModuleFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1a3884] bg-gray-50 focus:bg-white min-w-[140px] outline-none"
                        >
                            <option value="">All Modules</option>
                            <option value="Auth">Authentication</option>
                            <option value="Assessment">Assessment</option>
                            <option value="College">College</option>
                            <option value="Course">Course</option>
                            <option value="System">System</option>
                            <option value="User">User</option>
                        </select>

                        {moduleFilter && (
                            <button
                                onClick={() => setModuleFilter('')}
                                className="px-4 py-2 text-sm text-gray-600 hover:text-red-600 font-medium transition-colors"
                            >
                                Reset
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Logs Table */}
            <div className="card-hover overflow-hidden">
                <h2 className="text-base font-bold text-gray-900 mb-3">Activity Timeline</h2>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                            <tr>
                                <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-700 uppercase tracking-wider">Timestamp</th>
                                <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-700 uppercase tracking-wider">User</th>
                                <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-700 uppercase tracking-wider">Action</th>
                                <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-700 uppercase tracking-wider">Module</th>
                                <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-700 uppercase tracking-wider">Details</th>
                                <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-700 uppercase tracking-wider">IP Address</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-4 py-8 text-center">
                                        <div className="flex justify-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-200 border-t-indigo-600"></div>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredLogs.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                        <Activity className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                                        <p className="text-sm font-medium">No logs found</p>
                                    </td>
                                </tr>
                            ) : (
                                currentLogs.map((log) => (
                                    <tr key={log._id} className="hover:bg-gradient-to-r hover:from-indigo-50 hover:to-indigo-50 transition-all duration-200">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <Clock className="w-3 h-3 text-gray-400" />
                                                <div>
                                                    <div className="text-xs font-semibold text-gray-900">
                                                        {format(new Date(log.createdAt), 'MMM dd, yyyy')}
                                                    </div>
                                                    <div className="text-[10px] text-gray-500">
                                                        {format(new Date(log.createdAt), 'hh:mm:ss a')}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                                                    {log.user?.fullName?.charAt(0) || 'U'}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-bold text-gray-900">{log.user?.fullName || 'Unknown User'}</div>
                                                    <div className="text-xs text-gray-500">{log.user?.email || 'N/A'}</div>
                                                    <div className="text-[10px] text-gray-400 capitalize">{log.user?.role || 'N/A'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">{getActionBadge(log.action)}</td>
                                        <td className="px-4 py-3">
                                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                                                {log.module}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="text-xs text-gray-600">{log.details || 'N/A'}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1">
                                                <MapPin className="w-3 h-3 text-gray-400" />
                                                <span className="text-xs font-mono text-gray-600">{log.ipAddress || 'N/A'}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Block */}
                {totalPages > 1 && (
                    <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                        <div className="text-xs text-gray-500">
                            Page {currentPage} of {totalPages} • Showing {currentLogs.length} of {filteredLogs.length} logs
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 border border-gray-300 dark:border-[#1a3884]/40 rounded-lg text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#002d5c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Previous
                            </button>

                            <div className="flex items-center space-x-1">
                                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNum = totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }

                                    return (
                                        <button
                                            key={i}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${currentPage === pageNum
                                                ? 'bg-indigo-600 text-white shadow-sm'
                                            : 'bg-gray-50 dark:bg-[#002147] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#002d5c] border border-gray-300 dark:border-[#1a3884]/40'
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 border border-gray-300 dark:border-[#1a3884]/40 rounded-lg text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#002d5c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CaseLogList;
